import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule } from '@angular/forms';
import { PromptService } from '@k12/app-services';
import {
  ComponentType,
  FileUploadViewModel,
  InputType,
  PromptComponent,
  PromptFileAttachment,
  PromptInputOption
} from '@k12/models';
import { CfiFileUploadComponent } from '@k12/uploads';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CfiFileUploadComponent, FormsModule],

  selector: 'lib-pc-file-upload',
  styleUrl: './pc-file-upload.component.scss',
  templateUrl: './pc-file-upload.component.html'
})
export class PcFileUploadComponent implements OnInit, AfterViewInit {
  @Input() allowPointerEvents!: boolean;
  @Input() configJson: PromptComponent = {
    header: '',
    instructions: '',
    order: 0,
    inputType: ComponentType.FileUpload,
    componentType: ComponentType.FileUpload,
    label: '',
    inputs: [
      {
        dataType: InputType.File,
        inputType: ComponentType.FileUpload,
        instructions: '',
        isRequired: true,
        fileSize: 0,
        fileType: [],
        max: 0,
        order: 0,
        label: '',
        options: [] // Set files and max size in code for now
      }
    ]
  };

  @Input() enrollmentBuilderView!: boolean;
  @Input() formGroup!: FormGroup;
  @Input() isEnabled: boolean = true;

  /**
   * The enrollment session the files belong to. Empty in the admin builder, where the component is
   * previewed rather than answered; that case keeps the legacy file-name behaviour so the preview
   * does not attempt an upload against a session that does not exist.
   */
  @Input() sessionId: string = '';

  @Output() cbConfig = new EventEmitter();
  // Emit the stored attachment ids to the parent component
  @Output() fileUploadValuesEmitter = new EventEmitter<{ formName: string; control: Record<string, unknown> }>();

  public vm!: FileUploadViewModel;

  @ViewChild(CfiFileUploadComponent) private fileUpload?: CfiFileUploadComponent;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly promptService: PromptService = inject(PromptService);

  private selectedFiles: File[] = [];

  /** Attachment id per selected file, keyed the same way the child de-duplicates. */
  private readonly attachments = new Map<string, PromptFileAttachment>();

  private readonly uploading = new Set<string>();

  public ngOnInit(): void {
    this.cbConfig.emit(this.configJson);
  }

  public ngAfterViewInit(): void {
    this.rehydrateExistingFiles();
  }

  /**
   * Use when child emits only newly added files (delta).
   * Handles single or multiple files in one add action.
   */
  public onFilesAdded(addedFiles: File[]): void {
    const merged = this.mergeFiles(this.selectedFiles, addedFiles);
    this.syncFilesState(merged);

    if (!this.canUpload()) {
      return;
    }

    const pending = this.selectedFiles.filter((file) => !this.attachments.has(this.fileKey(file)));
    pending.forEach((file) => this.uploadFile(file));
  }

  /**
   * Use when child emits full current list after remove (or any change).
   */
  public onFilesChanged(currentFiles: File[]): void {
    const removed = this.selectedFiles.filter(
      (file) => !currentFiles.some((current) => this.fileKey(current) === this.fileKey(file))
    );

    this.syncFilesState(currentFiles);

    // Drop the blob as well, or removing a file from the drop zone leaves it stored against the
    // session and still counting toward the input's file limit.
    removed.forEach((file) => this.deleteFile(file));
  }

  /** Re-sends any selected file that has no attachment yet. */
  public onRetryRequested(): void {
    if (!this.canUpload()) {
      return;
    }

    this.selectedFiles
      .filter((file) => !this.attachments.has(this.fileKey(file)))
      .forEach((file) => this.uploadFile(file));
  }

  private uploadFile(file: File): void {
    const inputId = this.configJson?.inputs?.[0]?.id;
    const key = this.fileKey(file);
    if (!inputId || this.uploading.has(key)) {
      return;
    }

    this.uploading.add(key);
    this.fileUpload?.setUploading(file);
    this.emitControlValue();

    this.promptService
      .uploadPromptFile(this.sessionId, inputId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (event: HttpEvent<PromptFileAttachment>) => {
          if (event.type === HttpEventType.Response && event.body) {
            this.uploading.delete(key);
            this.attachments.set(key, event.body);
            if (!this.selectedFiles.some((current) => this.fileKey(current) === key)) {
              this.deleteFile(file);
            } else {
              this.fileUpload?.setUploadResult(file, true);
            }
            this.emitControlValue();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.uploading.delete(key);
          this.fileUpload?.setUploadResult(file, false, this.uploadErrorMessage(error));
          this.emitControlValue();
        }
      });
  }

  public onViewRequested(file: File): void {
    const attachment = this.attachments.get(this.fileKey(file));
    const inputId = this.configJson?.inputs?.[0]?.id;
    if (!attachment || !inputId || !this.canUpload()) {
      return;
    }

    const tab = window.open('', '_blank');

    this.promptService
      .downloadPromptFile(this.sessionId, inputId, attachment.attachmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          if (tab) {
            tab.location.href = url;
          } else {
            window.open(url, '_blank');
          }
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        },
        error: () => {
          tab?.close();
          this.fileUpload?.rejectedFileMessage.set('Unable to open the file. Please try again.');
        }
      });
  }

  private deleteFile(file: File): void {
    const key = this.fileKey(file);
    const attachment = this.attachments.get(key);
    const inputId = this.configJson?.inputs?.[0]?.id;

    if (!attachment || !inputId || !this.canUpload()) {
      this.attachments.delete(key);
      return;
    }

    this.attachments.delete(key);
    this.emitControlValue();

    this.promptService
      .deletePromptFile(this.sessionId, inputId, attachment.attachmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  /**
   * Seeds the drop zone from what is already stored for this input so returning to the prompt does
   * not present an empty required field. Sizes are unknown from metadata alone, so the placeholders
   * carry the name only.
   */
  private rehydrateExistingFiles(): void {
    const inputId = this.configJson?.inputs?.[0]?.id;
    if (!this.canUpload() || !inputId) {
      return;
    }

    this.promptService
      .getPromptFiles(this.sessionId, inputId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (existing) => {
          if (existing.length === 0) {
            return;
          }

          const files = existing.map(
            (attachment, index) => new File([], attachment.fileName, { lastModified: index })
          );
          files.forEach((file, index) => this.attachments.set(this.fileKey(file), existing[index]));

          this.selectedFiles = files;
          this.fileUpload?.vm.files.set(files);
          files.forEach((file) => this.fileUpload?.setUploadResult(file, true));
          this.configJson = this.getUpdatedConfigWithFileNames(files.map((file) => file.name));
          this.cbConfig.emit(this.configJson);
          this.emitControlValue();
        },
        error: () => undefined
      });
  }

  /**
   * Syncs the internal state of selected files and emits the updated
   * config and form control values.
   * @param files The current list of selected files.
   */
  private syncFilesState(files: File[]): void {
    const maxFiles = this.getMaxFiles();
    const dedupedFiles = this.dedupeFiles(files);
    this.selectedFiles = maxFiles > 0 ? dedupedFiles.slice(0, maxFiles) : dedupedFiles;

    const fileNames = this.selectedFiles.map((file) => file.name);
    this.configJson = this.getUpdatedConfigWithFileNames(fileNames);
    this.cbConfig.emit(this.configJson);

    this.emitControlValue();
  }

  /**
   * The control holds attachment ids once the files are stored, so the submitted answer resolves to
   * a blob. Before an upload completes -- and in the builder preview, which has no session -- it
   * falls back to file names, which is what this component has always emitted.
   */
  private emitControlValue(): void {
    const formName = this.configJson?.id;
    const inputId = this.configJson?.inputs?.[0]?.id;
    if (!formName || !inputId) {
      return;
    }

    let values: string[] = this.selectedFiles.map((file) => file.name);
    if (this.canUpload()) {
      values = this.uploading.size > 0
        ? []
        : this.selectedFiles
            .map((file) => this.attachments.get(this.fileKey(file))?.attachmentId)
            .filter((id): id is string => !!id);
    }

    this.fileUploadValuesEmitter.emit({
      formName,
      control: { [inputId]: values.length > 0 ? values : '' }
    });
  }

  private canUpload(): boolean {
    return !!this.sessionId && !this.enrollmentBuilderView;
  }

  private uploadErrorMessage(error: HttpErrorResponse): string {
    return error?.error?.message || 'Upload failed. Please try again.';
  }

  /**
   * Updates the config JSON with the current list of file names.
   * @param fileNames The current list of file names.
   * @returns The updated config JSON.
   */
  private getUpdatedConfigWithFileNames(fileNames: string[]): PromptComponent {
    const options: PromptInputOption[] = fileNames.map((name, index) => ({
      label: name,
      value: name,
      order: index,
      selected: true
    }));

    const updatedInputs = (this.configJson.inputs ?? []).map((input, index) =>
      index === 0 ? { ...input, options } : input
    );

    return {
      ...this.configJson,
      inputs: updatedInputs
    };
  }

  /**
   * Retrieves the maximum number of files allowed from the config JSON.
   * @returns The maximum number of files allowed.
   */
  private getMaxFiles(): number {
    return Number(this.configJson?.inputs?.[0]?.max ?? 0);
  }

  /**
   * Merges the existing and incoming files, removing duplicates.
   * @param existing The existing list of files.
   * @param incoming The incoming list of files.
   * @returns The merged list of files without duplicates.
   */
  private mergeFiles(existing: File[], incoming: File[]): File[] {
    return this.dedupeFiles([...existing, ...incoming]);
  }

  /**
   * Removes duplicate files based on name, size, and last modified date.
   * @param files The list of files to deduplicate.
   * @returns The deduplicated list of files.
   */
  private dedupeFiles(files: File[]): File[] {
    const byKey = new Map<string, File>();
    for (const file of files) {
      byKey.set(this.fileKey(file), file);
    }
    return Array.from(byKey.values());
  }

  /**
   * Generates a unique key for a file based on its name, size, and last modified date.
   * @param file The file to generate a key for.
   * @returns The unique key for the file.
   */
  private fileKey(file: File): string {
    return `${file.name}::${file.size}::${file.lastModified}`;
  }
}
